import { clear, dayLabel, el, lapTime, ordinal, totalDistance } from './dom.js';
import { plural, serverText, t } from '../i18n/i18n.js';

/**
 * Everybody's record, in one dialog.
 *
 * The account panel answers "how am I doing". This answers the question that immediately
 * follows, which is "compared to whom", and it is a different question in at least three
 * ways — so this is four boards behind four tabs rather than one ranked list:
 *
 * - **Points** is the overall answer and the default, for the reasons the server gives.
 * - **Wins** and **Distance** exist because they are the two numbers people quote at each
 *   other, and because somebody who has never won can still have driven the furthest.
 * - **Fastest times** is the one that actually settles arguments: the same region, the same
 *   seed, the same route, ranked by the clock. It is a separate tab and not a column
 *   because a time only means anything next to another time on the same road.
 *
 * **Signed out, you can read it and you are told why you are not on it.** Gating the read
 * would make it a private diary, and hiding the reason would make the empty space look
 * broken rather than like an invitation.
 *
 * **Fetched on open, every time.** A board is stale the moment somebody else finishes a
 * race, and this is one small request against a local table — caching it would only buy the
 * chance to show numbers that are quietly wrong.
 */

export type BoardKind = 'points' | 'wins' | 'distance' | 'region';

export interface BoardEntry {
  rank: number;
  name: string;
  points: number;
  wins: number;
  podiums: number;
  races: number;
  metres: number;
  cupWins: number;
  ms: number | null;
  setAt: number | null;
  you: boolean;
}

export interface Board {
  kind: BoardKind;
  region: string | null;
  regionLabel: string | null;
  total: number;
  rows: BoardEntry[];
  you: BoardEntry | null;
}

export interface BoardRegion {
  region: string;
  label: string;
  drivers: number;
  best: number;
}

interface BoardResponse {
  ok: boolean;
  error?: string;
  signedIn: boolean;
  board: Board;
  regions: BoardRegion[];
}

interface Tab {
  kind: BoardKind;
  label: string;
}

const TABS: Tab[] = [
  { kind: 'points', label: t('Points') },
  { kind: 'wins', label: t('Wins') },
  { kind: 'distance', label: t('Distance') },
  { kind: 'region', label: t('Fastest times') },
];

interface Column {
  head: string;
  value(entry: BoardEntry): string;
  /** The column the board is sorted by, picked out so the eye lands on it first. */
  lead?: boolean;
}

/**
 * What each board shows, beyond the rank and the name.
 *
 * Every board carries the number it is sorted by *and* the two that put it in context — a
 * points total with no race count is a number nobody can argue with, which is the opposite
 * of what a leaderboard is for.
 */
const COLUMNS: Record<BoardKind, Column[]> = {
  points: [
    { head: t('Points'), value: (e) => String(e.points), lead: true },
    { head: t('Wins'), value: (e) => String(e.wins) },
    { head: t('Races'), value: (e) => String(e.races) },
  ],
  wins: [
    { head: t('Wins'), value: (e) => String(e.wins), lead: true },
    { head: t('Podiums'), value: (e) => String(e.podiums) },
    { head: t('Races'), value: (e) => String(e.races) },
  ],
  distance: [
    { head: t('Distance'), value: (e) => totalDistance(e.metres), lead: true },
    { head: t('Races'), value: (e) => String(e.races) },
  ],
  region: [
    { head: t('Time'), value: (e) => (e.ms === null ? '—' : lapTime(e.ms)), lead: true },
    { head: t('Set'), value: (e) => (e.setAt ? dayLabel(e.setAt) : '—') },
  ],
};

export interface LeaderboardOptions {
  /** Signed out, the board's footer offers the way on; the app owns that dialog. */
  onSignIn(): void;
}

export class LeaderboardPanel {
  readonly badge: HTMLButtonElement;
  readonly root: HTMLElement;

  private readonly tabRow: HTMLElement;
  private readonly regionRow: HTMLElement;
  private readonly regionSelect: HTMLSelectElement;
  private readonly body: HTMLElement;
  private readonly footer: HTMLElement;

  private kind: BoardKind = 'points';
  private region: string | null = null;
  private regions: BoardRegion[] = [];
  private signedIn = false;
  /**
   * Which fetch the panel is still interested in.
   *
   * Clicking three tabs quickly starts three requests, and they can land in any order. The
   * counter means a slow first answer cannot overwrite a fast third one — the symptom of
   * which would be a table that disagrees with the tab above it.
   */
  private request = 0;

  constructor(private readonly options: LeaderboardOptions) {
    this.badge = el(
      'button',
      { class: 'corner-badge board-badge', type: 'button', id: 'leaderboard-badge', title: t('Everybody’s record') },
      t('Leaderboard'),
    ) as HTMLButtonElement;
    this.badge.addEventListener('click', () => this.open());

    this.tabRow = el('div', { class: 'board-tabs', id: 'board-tabs' });
    for (const tab of TABS) {
      const button = el(
        'button',
        { class: 'board-tab', type: 'button', dataset: { board: tab.kind } },
        tab.label,
      );
      button.addEventListener('click', () => this.show(tab.kind));
      this.tabRow.append(button);
    }

    this.regionSelect = el('select', {
      class: 'field board-region-select',
      id: 'board-region',
    }) as HTMLSelectElement;
    this.regionSelect.addEventListener('change', () => {
      this.region = this.regionSelect.value || null;
      void this.load();
    });
    this.regionRow = el(
      'label',
      { class: 'field-label board-region-row is-hidden', htmlFor: 'board-region' },
      t('Where'),
      this.regionSelect,
    );

    this.body = el('div', { class: 'board-body', id: 'board-body' });
    this.footer = el('p', { class: 'board-you', id: 'board-you' });

    const close = el('button', { class: 'button button-ghost', type: 'button' }, t('Close'));
    close.addEventListener('click', () => this.close());

    this.root = el(
      'div',
      { class: 'modal-scrim board-scrim is-hidden', id: 'leaderboard-modal' },
      el(
        'div',
        { class: 'modal-card' },
        el('h2', { class: 'modal-title' }, t('Global leaderboard')),
        this.tabRow,
        this.regionRow,
        this.body,
        this.footer,
        el('div', { class: 'modal-actions' }, close),
      ),
    );
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.close();
    });

    this.paintTabs();
  }

  get isOpen(): boolean {
    return !this.root.classList.contains('is-hidden');
  }

  open(): void {
    this.root.classList.remove('is-hidden');
    void this.load();
  }

  close(): void {
    this.root.classList.add('is-hidden');
  }

  /**
   * Somebody signed in, out, or finished a race.
   *
   * A refetch only while the panel is up: closed, the next `open` gets fresh numbers
   * anyway, and a background request for a dialog nobody is looking at is waste.
   */
  invalidate(): void {
    if (this.isOpen) void this.load();
  }

  private show(kind: BoardKind): void {
    if (this.kind === kind) return;
    this.kind = kind;
    this.paintTabs();
    void this.load();
  }

  private paintTabs(): void {
    for (const button of this.tabRow.querySelectorAll('.board-tab')) {
      button.classList.toggle('is-active', (button as HTMLElement).dataset.board === this.kind);
    }
    this.regionRow.classList.toggle('is-hidden', this.kind !== 'region');
  }

  private async load(): Promise<void> {
    const mine = ++this.request;
    // Left in place rather than cleared, so switching tabs does not flash an empty card at
    // somebody for the length of a request that is going to take twenty milliseconds.
    this.root.classList.add('is-loading');
    if (!this.body.firstChild) this.body.append(el('p', { class: 'panel-hint' }, t('Reading the board…')));

    const params = new URLSearchParams({ board: this.kind });
    // On a cold open straight into the region tab there is no list yet; the server answers
    // with an empty board and the regions, and `paint` picks one and asks again.
    if (this.kind === 'region' && this.region) params.set('region', this.region);

    try {
      const res = await fetch(`/api/leaderboard?${params}`);
      const body = (await res.json()) as BoardResponse;
      if (mine !== this.request) return;
      if (!res.ok || !body.ok) throw new Error(body.error ? serverText(body.error) : t('The board is not available.'));
      this.regions = body.regions ?? [];
      this.signedIn = Boolean(body.signedIn);
      this.paint(body.board);
    } catch (err) {
      if (mine !== this.request) return;
      clear(this.body);
      this.body.append(
        el('p', { class: 'form-error' }, err instanceof Error ? err.message : String(err)),
      );
      this.footer.textContent = '';
    } finally {
      if (mine === this.request) this.root.classList.remove('is-loading');
    }
  }

  private paint(board: Board): void {
    this.paintRegions(board);
    // Two ways to land here holding the wrong board: the region tab was opened before the
    // list existed and has only now chosen one, or the region that was showing has since
    // dropped off the list. Both end with the picker naming something the table below it is
    // not, which is worse than one more small request. Nothing is painted in between.
    if (this.kind === 'region' && this.region && this.region !== board.region) {
      void this.load();
      return;
    }

    clear(this.body);
    if (!board.rows.length) {
      this.body.append(el('p', { class: 'panel-hint' }, this.emptyText()));
      this.paintFooter(board);
      return;
    }

    const columns = COLUMNS[this.kind];
    const head = el(
      'tr',
      {},
      el('th', { class: 'board-rank' }, '#'),
      el('th', {}, t('Racer')),
      ...columns.map((c) => el('th', { class: c.lead ? 'board-lead' : '' }, c.head)),
    );

    const body = el('tbody', {});
    for (const entry of board.rows) body.append(this.row(entry, columns));
    // The caller's own row when it fell off the bottom, kept visually attached to the
    // table rather than dropped into the footer: it is a row of the same board.
    if (board.you) body.append(this.row(board.you, columns, true));

    this.body.append(el('table', { class: 'board-table' }, el('thead', {}, head), body));
    this.paintFooter(board);
  }

  private row(entry: BoardEntry, columns: Column[], detached = false): HTMLElement {
    return el(
      'tr',
      { class: `board-row${entry.you ? ' is-you' : ''}${detached ? ' is-detached' : ''}` },
      el('td', { class: 'board-rank tabular' }, String(entry.rank)),
      el('td', { class: 'board-name' }, entry.name),
      ...columns.map((c) =>
        el('td', { class: `tabular${c.lead ? ' board-lead' : ''}` }, c.value(entry)),
      ),
    );
  }

  private paintRegions(board: Board): void {
    // Rebuilt from scratch on every load: a region joins the list the first time anybody
    // sets a time there, so the list is as live as the board is.
    clear(this.regionSelect);
    for (const region of this.regions) {
      this.regionSelect.append(
        el(
          'option',
          { value: region.region },
          plural(region.drivers, '{label} · {n} driver', '{label} · {n} drivers', { label: region.label }),
        ),
      );
    }
    const chosen = board.region ?? this.region ?? this.regions[0]?.region ?? null;
    this.region = this.regions.some((r) => r.region === chosen) ? chosen : this.regions[0]?.region ?? null;
    if (this.region) this.regionSelect.value = this.region;
    this.regionSelect.disabled = this.regions.length === 0;
  }

  private emptyText(): string {
    if (this.kind === 'region') {
      return this.regions.length
        ? t('Nobody has set a time here yet.')
        : t('No region has a recorded time yet. Finish a race while signed in and this fills up.');
    }
    return t('Nobody has raced while signed in yet. The first person to finish one takes the top of this board.');
  }

  private paintFooter(board: Board): void {
    clear(this.footer);
    if (!this.signedIn) {
      const button = el(
        'button',
        { class: 'button button-ghost board-signin', type: 'button' },
        t('Sign in'),
      );
      button.addEventListener('click', () => {
        this.close();
        this.options.onSignIn();
      });
      this.footer.append(
        el('span', {}, t('Only signed-in races are counted. ')),
        button,
      );
      return;
    }

    const mine = board.you ?? board.rows.find((r) => r.you) ?? null;
    if (mine) {
      this.footer.append(
        el('span', { class: 'board-you-strong' }, t('You are {rank}', { rank: ordinal(mine.rank) })),
        el('span', {}, t(' of {total}.', { total: board.total })),
      );
      return;
    }
    this.footer.append(
      el(
        'span',
        {},
        this.kind === 'region'
          ? t('You have no time here yet. Race this region and it lands on the board.')
          : t('You are not on this board yet — finish a race while signed in.'),
      ),
    );
  }
}
